/**
 * Gestion des utilisateurs : création de comptes nominatifs, modification du profil et du
 * périmètre, activation/désactivation (`06-authentification-et-droits.md`, `20-formulaires.md`).
 *
 * Pas de champ mot de passe : Cognito envoie l'invitation et impose le changement à la première
 * connexion. Un administrateur ne peut pas se désactiver lui-même — le serveur le refuse (`409`), ce
 * message est donc celui du serveur, pas une invention du frontend.
 */
import { useState } from "react";

import { useIdentite } from "../../shared/auth/contexte";
import { ErreurApi } from "../../shared/api/erreurs";
import { PROFILS, type Profil } from "../../shared/api/types";
import { Chargement, Erreur } from "../../shared/composants/Etats";
import { useCreerUtilisateur, useModifierUtilisateur, useUtilisateurs } from "./requetes";

const LIBELLE_PROFIL: Record<Profil, string> = {
  "admin-totalenergies": "Administrateur TotalEnergies",
  "operationnel-totalenergies": "Opérationnel TotalEnergies",
  "air-tahiti": "Air Tahiti",
  support: "Support",
};

export function EcranUtilisateurs() {
  const identite = useIdentite();
  const requete = useUtilisateurs();
  const creer = useCreerUtilisateur();
  const modifier = useModifierUtilisateur();

  const [email, definirEmail] = useState("");
  const [nomComplet, definirNom] = useState("");
  const [profil, definirProfil] = useState<Profil>("operationnel-totalenergies");
  const [clientId, definirClientId] = useState("");

  async function soumettre(evenement: React.FormEvent) {
    evenement.preventDefault();
    await creer.mutateAsync({
      email,
      nom_complet: nomComplet,
      profil,
      client_id: profil === "air-tahiti" ? clientId || null : null,
    });
    definirEmail("");
    definirNom("");
    definirClientId("");
  }

  if (requete.isPending) return <Chargement libelle="Chargement des comptes…" />;
  if (requete.isError) return <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />;

  return (
    <section>
      <h1>Utilisateurs</h1>

      <form className="formulaire-ligne" onSubmit={(e) => void soumettre(e)}>
        <label htmlFor="email-utilisateur">Email</label>
        <input
          id="email-utilisateur"
          type="email"
          required
          value={email}
          onChange={(e) => definirEmail(e.target.value)}
        />
        <label htmlFor="nom-utilisateur">Nom complet</label>
        <input
          id="nom-utilisateur"
          required
          minLength={1}
          value={nomComplet}
          onChange={(e) => definirNom(e.target.value)}
        />
        <label htmlFor="profil-utilisateur">Profil</label>
        <select
          id="profil-utilisateur"
          value={profil}
          onChange={(e) => definirProfil(e.target.value as Profil)}
        >
          {PROFILS.map((p) => (
            <option key={p} value={p}>
              {LIBELLE_PROFIL[p]}
            </option>
          ))}
        </select>
        {profil === "air-tahiti" && (
          <>
            <label htmlFor="client-utilisateur">Client</label>
            <input
              id="client-utilisateur"
              required
              value={clientId}
              onChange={(e) => definirClientId(e.target.value)}
            />
          </>
        )}
        <button type="submit" disabled={creer.isPending}>
          Créer le compte
        </button>
        {creer.isError && (
          <span role="alert">
            {creer.error instanceof ErreurApi ? creer.error.message : "La création a échoué."}
          </span>
        )}
      </form>

      <table className="tableau">
        <thead>
          <tr>
            <th scope="col">Email</th>
            <th scope="col">Nom</th>
            <th scope="col">Profil</th>
            <th scope="col">Client</th>
            <th scope="col">Actif</th>
            <th scope="col"></th>
          </tr>
        </thead>
        <tbody>
          {requete.data.data.map((compte) => (
            <tr key={compte.sub}>
              <td>{compte.email}</td>
              <td>{compte.nom_complet ?? "—"}</td>
              <td>{LIBELLE_PROFIL[compte.profil as Profil] ?? compte.profil}</td>
              <td>{compte.client_id ?? "—"}</td>
              <td>{compte.actif ? "oui" : "non"}</td>
              <td>
                <button
                  type="button"
                  disabled={modifier.isPending || compte.sub === identite.sub}
                  onClick={() =>
                    modifier.mutate({ sub: compte.sub, corps: { actif: !compte.actif } })
                  }
                  title={
                    compte.sub === identite.sub
                      ? "Vous ne pouvez pas désactiver votre propre compte."
                      : undefined
                  }
                >
                  {compte.actif ? "Désactiver" : "Réactiver"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {modifier.isError && (
        <p role="alert">
          {modifier.error instanceof ErreurApi ? modifier.error.message : "La modification a échoué."}
        </p>
      )}

      <p>
        Quatre profils déterminent les droits d'accès : <strong>Administrateur TotalEnergies</strong>{" "}
        (accès complet, seul profil pouvant gérer les utilisateurs, le paramétrage et l'audit),{" "}
        <strong>Opérationnel TotalEnergies</strong> (tout le parc et les opérations courantes —
        fiche de vie, maintenance, alertes —, sans accès au paramétrage), <strong>Air Tahiti</strong>{" "}
        (uniquement les GRV affectés à ce client, avec une localisation agrégée plutôt que la
        position précise lorsqu'un GRV se trouve à Tahiti) et <strong>Support</strong> (diagnostic
        technique — parc trackers, incidents —, sans accès aux données métier complètes). Un compte
        appartient à un seul profil.
      </p>
    </section>
  );
}
